import { AutocompleteWithApply } from '@components/AutocompleteWithApply';
import { facilitiesApiSlice, IFacilitiesFilter } from '@src/api';
import { PAGE_SIZE } from '@src/api/common';
import { IntersectionType } from '@src/api/types';
import { useDebounce } from '@src/hooks/useDebounce';
import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '@src/store';

const initialFacilityFilter = {
  search: '',
  skip: 0,
  take: PAGE_SIZE,
};

export const FacilitySelect = ({ disabled = false }) => {
  const { watch, setValue } = useFormContext();
  const scope = useSelector((state: RootState) => state.selectionScope);
  const { selectionId } = useParams();
  const [facilityFilter, setFacilityFilter] = useState<Nullable<Nullable<any>>>(null);
  const [initialIntersectionType, setInitialIntersectionType] = useState<Nullable<IntersectionType>>();

  const debouncedFacilityFilter = useDebounce(facilityFilter);

  const [
    salesRepresentativeId,
    laboratoryIntersectionType,
    laboratoryIds,
    intersectionType,
    intersectionArr,
    addedLaboratoryIds,
    removedLaboratoryIds,
    addedIdsArr,
    removedIdsArr,
  ] = watch([
    'createVisibility.salesRepresentativeId',
    'createVisibility.laboratoryIntersectionType',
    'createVisibility.laboratoryIds',
    'createVisibility.facilityIntersectionType',
    'createVisibility.facilityVisibilities',
    'createVisibility.addedLaboratoryIds',
    'createVisibility.removedLaboratoryIds',
    'createVisibility.addedFacilityIds',
    'createVisibility.removedFacilityIds',
  ]);

  useEffect(() => {
    setFacilityFilter(initialFacilityFilter);
  }, [laboratoryIntersectionType, laboratoryIds, addedLaboratoryIds, removedLaboratoryIds]);

  const [value, setVal] = useState({ intersectionType, ids: intersectionArr });

  useEffect(() => {
    if (scope?.isAllOrderingProviders) {
      setVal({ intersectionType: IntersectionType.Exclude, ids: [] });
      setInitialIntersectionType(IntersectionType.Exclude);
    } else setInitialIntersectionType(IntersectionType.Include);
  }, [scope, selectionId]);

  useEffect(() => {
    if (intersectionType) setVal({ intersectionType, ids: intersectionArr });
  }, [intersectionType, intersectionArr]);

  const withoutLabsIds =
    (!addedLaboratoryIds || addedLaboratoryIds?.length === 0) &&
    (!removedLaboratoryIds || removedLaboratoryIds?.length === 0) &&
    (!laboratoryIds || laboratoryIds?.length === 0) &&
    (!laboratoryIntersectionType || laboratoryIntersectionType === IntersectionType.None);

  useEffect(() => {
    setFacilityFilter(prev =>
      prev
        ? {
            ...prev,
            laboratoryIntersectionType:
              withoutLabsIds && scope?.isAllLaboratories
                ? IntersectionType.Exclude
                : laboratoryIntersectionType || IntersectionType.None,
            laboratoryIds: laboratoryIds || [],
            selectionId: selectionId && !scope.visibilityIsChanged ? +selectionId : undefined,
            addedLaboratoryIds: addedLaboratoryIds || undefined,
            removedLaboratoryIds: removedLaboratoryIds || undefined,
          }
        : null,
    );
  }, [
    scope?.isAllLaboratories,
    scope.visibilityIsChanged,
    scope.labsIsChange,
    addedLaboratoryIds,
    laboratoryIds,
    laboratoryIntersectionType,
    removedLaboratoryIds,
    selectionId,
    withoutLabsIds,
  ]);

  const { facitilies, isEnd, isFetching } = facilitiesApiSlice.useGetFacilitiesQuery(
    { ...debouncedFacilityFilter, salesRepresentativeId: salesRepresentativeId || null } as IFacilitiesFilter,
    {
      skip: !debouncedFacilityFilter,
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ data, ...rest }) => {
        return {
          ...rest,
          isEnd: data?.isEnd,
          facitilies: [
            ...(data?.data.map(({ id, name, orderingProviders, isSelected }) => ({
              name,
              value: id,
              providers: orderingProviders || [],
              isSelected,
            })) || []),
          ],
        };
      },
    },
  );

  const onApply = (ids, addedIds, removedIds) => {
    const intersecionTypeIsNone =
      ((addedIds && addedIds?.length > 0) || (removedIds && removedIds.length > 0) || ids.length === 0) &&
      !scope?.isAllOrderingProviders;

    const intersectionTypeFacility = intersecionTypeIsNone
      ? IntersectionType.None
      : scope?.isAllOrderingProviders
      ? IntersectionType.Exclude
      : IntersectionType.Include;

    setValue('createVisibility.facilityIntersectionType', intersectionTypeFacility);
    setValue('createVisibility.facilityVisibilities', ids);
    setValue('createVisibility.addedFacilityVisibilities', addedIds);
    setValue('createVisibility.removedFacilityVisibilities', removedIds);
  };

  const onSearch = useCallback((val: string) => {
    if (val?.length > 1 || val?.length < 1)
      setFacilityFilter(prev => (prev ? { ...prev, skip: 0, search: val || '' } : null));
  }, []);

  return (
    <AutocompleteWithApply
      noOptionsText="You don't have any facilities and ordering providers yet."
      options={
        facitilies && facitilies.length > 0
          ? [{ name: 'All accounts', value: 'all', isSelected: scope?.isAllOrderingProviders }, ...facitilies]
          : []
      }
      value={value}
      placeholder="Ordering providers"
      inputPlaceholder="accounts"
      onSearch={onSearch}
      isEnd={isEnd}
      loading={isFetching}
      onApply={onApply}
      parentName="facilityId"
      childName="orderingProviderIds"
      withChildren={true}
      setValuesFilter={setFacilityFilter}
      salesRepresentativeId={salesRepresentativeId}
      addedArr={addedIdsArr}
      removedArr={removedIdsArr}
      initialIntersectionType={initialIntersectionType}
      intersectionTypeFormValue={intersectionType}
      searchValue={facilityFilter?.search}
      disabled={disabled}
      scopeName="isAllOrderingProviders"
    />
  );
};
